import { colors } from '@theme/colors';

export type ColorScale = {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
};

const DARK_TEXT = '#29323A';
const LIGHT_TEXT = '#FFFFFF';
const DARK_TEXT_SECONDARY = '#52595F';
const LIGHT_TEXT_SECONDARY = '#B0BEC5';

function hexToHsl(hex: string): [number, number, number] {
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h = 0,
    s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }
  return [h * 360, s * 100, l * 100];
}

function hslToHex(h: number, s: number, l: number): string {
  h /= 360;
  s /= 100;
  l /= 100;
  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return (
    '#' +
    [r, g, b]
      .map((x) => Math.round(x * 255).toString(16).padStart(2, '0'))
      .join('')
  );
}

/**
 * WCAG 2.1 relative luminance.
 * @see https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */
export function getRelativeLuminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const toLinear = (c: number) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/**
 * WCAG 2.1 contrast ratio between two hex colors (range 1–21).
 * @see https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio
 */
export function getContrastRatio(fg: string, bg: string): number {
  const l1 = getRelativeLuminance(fg);
  const l2 = getRelativeLuminance(bg);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Generates a color scale from a base hex color.
 * Returns an object with keys 50-900 (like Tailwind),
 * keeping the hue and saturation constant while varying lightness.
 */
export function generateColorScale(hex: string): ColorScale {
  const [h, s] = hexToHsl(hex);

  const stops: Record<number, number> = {
    50: 95,
    100: 90,
    200: 80,
    300: 70,
    400: 60,
    500: 50,
    600: 40,
    700: 30,
    800: 20,
    900: 10,
  };

  return Object.fromEntries(
    Object.entries(stops).map(([key, lightness]) => [
      Number(key),
      hslToHex(h, s, lightness),
    ])
  ) as ColorScale;
}

/**
 * Picks the best contrast text color (dark or white) for a background,
 * using actual WCAG contrast ratio instead of a luminance threshold.
 */
export function getContrastText(background: string): string {
  const darkRatio = getContrastRatio(DARK_TEXT, background);
  const lightRatio = getContrastRatio(LIGHT_TEXT, background);
  return darkRatio >= lightRatio ? DARK_TEXT : LIGHT_TEXT;
}

/**
 * Adjusts lightness of a foreground color until it meets the WCAG AA
 * contrast ratio against a background. Preserves hue and saturation.
 * @param minRatio 4.5 for normal text (AA), 3.0 for large text / UI elements
 */
export function ensureContrast(
  foreground: string,
  background: string,
  minRatio: number = 4.5
): string {
  if (getContrastRatio(foreground, background) >= minRatio) {
    return foreground;
  }

  const bgLum = getRelativeLuminance(background);
  const [h, s, l] = hexToHsl(foreground);
  const step = bgLum > 0.179 ? -2 : 2;

  let adjustedL = l;
  for (let i = 0; i < 50; i++) {
    adjustedL = Math.max(0, Math.min(100, adjustedL + step));
    const candidate = hslToHex(h, s, adjustedL);
    if (getContrastRatio(candidate, background) >= minRatio) {
      return candidate;
    }
    if (adjustedL <= 0 || adjustedL >= 100) break;
  }

  return bgLum > 0.179 ? '#000000' : '#FFFFFF';
}

export interface AccessibleTextColors {
  primary: string;
  secondary: string;
  heading: string;
  body: string;
  muted: string;
  caption: string;
  link: string;
  success: string;
  error: string;
  warning: string;
}

/**
 * Derives a full set of accessible text colors for a given background,
 * ensuring WCAG AA compliance (4.5:1 for normal text, 3:1 for large).
 * @param _accentColor Reserved for future use (e.g. primary-tinted accents); hyperlink
 * color uses the standard UI blue so links stay consistent across company themes.
 */
export function deriveAccessibleTextColors(
  background: string,
  _accentColor: string
): AccessibleTextColors {
  const isLightBg = getRelativeLuminance(background) > 0.179;

  const primary = isLightBg ? DARK_TEXT : LIGHT_TEXT;
  const secondary = isLightBg ? DARK_TEXT_SECONDARY : LIGHT_TEXT_SECONDARY;

  return {
    primary,
    secondary,
    heading: primary,
    body: primary,
    muted: secondary,
    caption: secondary,
    link: ensureContrast(colors.blue[500], background, 4.5),
    success: ensureContrast('#1E8528', background, 4.5),
    error: ensureContrast('#D93535', background, 4.5),
    warning: ensureContrast('#F59E0B', background, 3),
  };
}
